import { useState } from 'react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Bespoke = () => {
  const [showContactModal, setShowContactModal] = useState(false);
  const [contactForm, setContactForm] = useState({ name: '', email: '', phone: '', company: '', message: '' });
  const [contactSubmitting, setContactSubmitting] = useState(false);
  const [contactSuccess, setContactSuccess] = useState(false);

  const handleContactSubmit = async (e) => {
    e.preventDefault();
    setContactSubmitting(true);
    const fullMessage = `[Enquiry Type: Consultant/Custom WMP Service]${contactForm.company ? `\n[Company: ${contactForm.company}]` : ''}\n\n${contactForm.message}`;
    try {
      const response = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ name: contactForm.name, email: contactForm.email, phone: contactForm.phone || '', message: fullMessage })
      });
      if (!response.ok) throw new Error('Failed');
      setContactSuccess(true);
      setTimeout(() => { setShowContactModal(false); setContactSuccess(false); setContactForm({ name: '', email: '', phone: '', company: '', message: '' }); }, 2000);
    } catch (err) {
      alert('Failed to send. Please email inquiry@plangenerator.com.au directly.');
    }
    setContactSubmitting(false);
  };

  const projectTypes = [
    'Residential and Commercial',
    'Mixed-use, Urban Multi-unit Projects',
    'Medical Facilities and Aged Care',
    'Hospitals; including Private and Public',
    'Retail and High Density Commercial',
    'Industrial and Factory Facilities',
    'Transport and Storage Facilities'
  ];

  const applicationTypes = [
    { name: 'Development Application', abbr: 'DA' },
    { name: 'Construction Certificate', abbr: 'CC' },
    { name: 'Complying Development Certificate', abbr: 'CDC' },
    { name: 'State Significant Development', abbr: 'SSDA' },
    { name: 'Green Star Development Credits', abbr: 'Green Star' }
  ];

  return (
    <div className="min-h-screen bg-[#F8FAFC]" style={{ fontFamily: "'IBM Plex Sans', system-ui, sans-serif" }}>
      <SEO
        title="Consultant Waste Management Plans | Custom WMP Services Australia"
        description="Expert-written, tailored Waste Management Plans for complex developments. 7+ years experience across NSW and VIC. DA, CC, CDC, SSDA compliant."
        keywords="consultant waste management plan, custom WMP, waste consultant Australia, NSW waste plan, VIC waste plan"
        canonicalPath="/services"
        noIndex
      />
      <link href="https://fonts.googleapis.com/css2?family=IBM+Plex+Sans:wght@300;400;500;600;700&display=swap" rel="stylesheet" />

      {/* Navbar */}
      <nav className="fixed top-0 w-full z-50 backdrop-blur-2xl bg-white/70 border-b border-white/40 shadow-sm">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-16">
          <Link to="/" className="text-xl font-bold tracking-tight text-[#0A2540]">PlanGen<span className="text-blue-600">Consulting</span></Link>
          <div className="hidden md:flex items-center gap-8 text-sm text-slate-600">
            <Link to="/" className="hover:text-[#0A2540] transition-colors">Home</Link>
            <a href="#about" className="hover:text-[#0A2540] transition-colors">About</a>
            <a href="#services" className="hover:text-[#0A2540] transition-colors">Services</a>
            <a href="#contact-section" className="hover:text-[#0A2540] transition-colors">Contact</a>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => setShowContactModal(true)} className="hidden sm:inline-flex px-4 py-1.5 rounded-full border border-amber-200 text-amber-700 text-sm font-medium hover:bg-amber-50 transition-colors" data-testid="nav-request-quote">
              Request a Quote
            </button>
            <Link to="/register" className="px-5 py-1.5 rounded-full bg-[#0A2540] text-white text-sm font-medium hover:bg-black transition-colors">Get started</Link>
          </div>
        </div>
      </nav>

      {/* Hero */}
      <section className="relative pt-28 pb-20 overflow-hidden">
        <div className="absolute inset-0 -z-10">
          <div className="absolute top-20 right-[20%] w-[350px] h-[350px] bg-amber-100/40 rounded-full blur-[120px]" />
          <div className="absolute bottom-0 left-[10%] w-[300px] h-[300px] bg-blue-100/30 rounded-full blur-[100px]" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-amber-50 text-amber-700 text-xs font-semibold mb-5 tracking-wide border border-amber-200">
              <svg className="w-3.5 h-3.5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M5.05 4.05a7 7 0 119.9 9.9L10 18.9l-4.95-4.95a7 7 0 010-9.9zM10 11a2 2 0 100-4 2 2 0 000 4z" clipRule="evenodd" /></svg>
              ESPECIALLY RECOMMENDED FOR NSW + VIC PROJECTS
            </div>
            <h1 className="text-4xl lg:text-5xl font-black tracking-tighter leading-[1.1] text-[#0A2540] mb-5">
              Consultant Waste<br />
              <span className="bg-gradient-to-r from-amber-600 to-orange-500 bg-clip-text text-transparent">Management Plans.</span>
            </h1>
            <p className="text-lg text-slate-700 mb-3 font-semibold">Expert-Written. Tailored. Compliant.</p>
            <p className="text-base text-slate-500 max-w-2xl mb-8 leading-relaxed">
              We undertake comprehensive site waste assessments to measure and evaluate current waste management performance against regulatory requirements and industry benchmarks. Detailed operational reviews and waste audits identify generation patterns, material recovery opportunities, and systemic inefficiencies.
            </p>
            <div className="flex items-center gap-4">
              <button onClick={() => setShowContactModal(true)} className="px-7 py-3 rounded-full bg-amber-600 text-white font-medium text-sm hover:bg-amber-700 transition-colors shadow-lg shadow-amber-600/20" data-testid="hero-request-quote-btn">
                Request a Quote
              </button>
              <Link to="/register" className="px-7 py-3 rounded-full border border-slate-200 text-[#0A2540] font-medium text-sm hover:bg-slate-50 transition-colors">
                Try Express Generator
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* NSW Callout */}
      <section className="pb-16">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-2xl p-7 flex items-start gap-5">
            <div className="w-11 h-11 bg-amber-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <svg className="w-5 h-5 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>
            </div>
            <div>
              <h3 className="text-base font-bold text-[#0A2540] mb-1.5">Why Consultant Plans for NSW + VIC?</h3>
              <p className="text-sm text-slate-600 leading-relaxed">New South Wales and Victoria have particularly complex waste management requirements that vary significantly between councils. The nuanced regulatory landscape, detailed council-specific guidelines, and stringent compliance standards mean that projects in these states often benefit from expert-written, tailored plans rather than automated generation.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Why Bespoke */}
      <section id="about" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-[1.3fr_1fr] gap-12 items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-3">WHY CONSULTANT PLANS</p>
              <h2 className="text-3xl lg:text-4xl font-bold tracking-tight text-[#0A2540] leading-tight mb-6">More than meeting regulatory requirements.</h2>
              <div className="space-y-4 text-sm text-slate-600 leading-relaxed">
                <p>Developing an Operational, Construction and Demolition Waste Management Plan is a proactive strategy to minimise waste, maximise recycling, and ensure responsible disposal. These plans highlight a commitment to sustainability while supporting the transition to a circular economy.</p>
                <p>For all Development Applications (DA, SSDA, CC and CDC), both Operational and C&amp;D Waste Management Plans are mandatory to demonstrate highly regulated and effective waste practices during and after the project.</p>
                <p>We've been planning, designing and implementing waste systems in NSW and VIC for <strong>over seven years</strong>. Technical expertise with practical industry knowledge ensures every plan is tailored to the unique needs of each specific project.</p>
              </div>
            </div>
            <div className="bg-[#0A2540] rounded-2xl p-8">
              <h3 className="text-lg font-bold text-white mb-5">Project Types We Cover</h3>
              <ul className="space-y-3">
                {projectTypes.map((type, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <svg className="w-4 h-4 text-amber-400 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                    <span className="text-sm text-slate-300">{type}</span>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Application Types */}
      <section id="services" className="py-16 bg-[#F8FAFC]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-blue-600 mb-3">SERVICES</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0A2540] tracking-tight">We Prepare WMPs For</h2>
            <p className="mt-3 text-base text-slate-500 max-w-xl mx-auto">A project-specific approach providing tailored, compliant, and sustainable waste management solutions.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
            {applicationTypes.map((type) => (
              <div key={type.abbr} className="bg-white rounded-2xl border border-slate-100 p-6 text-center hover:shadow-[0_12px_40px_rgb(0,0,0,0.08)] transition-all duration-300 hover:-translate-y-0.5">
                <div className="text-2xl font-black text-[#0A2540] mb-1">{type.abbr}</div>
                <div className="text-xs text-slate-500">{type.name}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-16 bg-[#0A2540]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="w-11 h-11 bg-blue-600 rounded-xl flex items-center justify-center mb-5">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Our Mission</h3>
              <p className="text-sm text-slate-400 leading-relaxed">Fast, compliant waste management plans for all developments in NSW and VIC. We specialise in preparing Op WMP and D&amp;C WMP that both meet and exceed council requirements -- simple, affordable and hassle free.</p>
            </div>
            <div className="bg-white/5 backdrop-blur-sm rounded-2xl p-8 border border-white/10">
              <div className="w-11 h-11 bg-amber-500 rounded-xl flex items-center justify-center mb-5">
                <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" /></svg>
              </div>
              <h3 className="text-xl font-bold text-white mb-3">Our Vision</h3>
              <p className="text-sm text-slate-400 leading-relaxed">A future where every development embraces smarter, more sustainable waste practices. We strive to make DA compliance simple and accessible while helping clients reduce waste, recover resources, and contribute to a cleaner, greener cityscape.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact-section" className="py-16 bg-[#F8FAFC]">
        <div className="max-w-3xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-amber-600 mb-3">GET IN TOUCH</p>
            <h2 className="text-3xl lg:text-4xl font-bold text-[#0A2540] tracking-tight">Ready for a Consultant Solution?</h2>
            <p className="mt-3 text-base text-slate-500">Tell us about your project and we'll provide a tailored quote.</p>
          </div>
          <div className="bg-white rounded-2xl border border-slate-100 shadow-[0_4px_24px_rgb(0,0,0,0.04)] p-8">
            <form onSubmit={handleContactSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Name *</label>
                  <input type="text" required value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="Your name" data-testid="bespoke-contact-name" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Email *</label>
                  <input type="email" required value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="you@example.com" data-testid="bespoke-contact-email" />
                </div>
              </div>
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Phone <span className="text-slate-400">(optional)</span></label>
                  <input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="04XX XXX XXX" data-testid="bespoke-contact-phone" />
                </div>
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1">Company <span className="text-slate-400">(optional)</span></label>
                  <input type="text" value={contactForm.company} onChange={(e) => setContactForm({...contactForm, company: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="Your company" data-testid="bespoke-contact-company" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Project Details *</label>
                <textarea required rows={4} value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none" placeholder="Location, development type, timeline, council..." data-testid="bespoke-contact-message" />
              </div>
              <button type="submit" disabled={contactSubmitting} className="px-8 py-2.5 rounded-full bg-amber-600 text-white text-sm font-medium hover:bg-amber-700 transition-colors disabled:opacity-50" data-testid="bespoke-contact-submit">
                {contactSubmitting ? 'Sending...' : 'Send Request'}
              </button>
              {contactSuccess && <p className="text-sm text-emerald-600 font-medium">Message sent! We'll be in touch soon.</p>}
            </form>
          </div>
        </div>
      </section>

      {/* CTA Footer */}
      <section className="py-16 bg-[#0A2540]">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4 tracking-tight">Need an Express Plan instead?</h2>
          <p className="text-slate-400 text-base mb-8">Our self-service generator creates council-compliant WMPs in under 30 minutes.</p>
          <div className="flex items-center justify-center gap-4">
            <Link to="/register" className="px-8 py-3 rounded-full bg-white text-[#0A2540] font-semibold text-sm hover:bg-slate-100 transition-colors shadow-lg">Start Generating</Link>
            <Link to="/" className="px-8 py-3 rounded-full border border-white/20 text-white font-medium text-sm hover:bg-white/10 transition-colors">Back to Home</Link>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-[#0A2540] border-t border-white/10 py-8">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4 text-xs text-slate-500">
            <span>Professional Indemnity &amp; Public Liability Insured</span>
            <span>&copy; {new Date().getFullYear()} PlanGen Consulting. All rights reserved.</span>
          </div>
        </div>
      </footer>

      {/* Contact Modal (kept for nav button) */}
      {showContactModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-[#0A2540]/60 backdrop-blur-sm" onClick={() => setShowContactModal(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 p-8 z-10">
            <button onClick={() => setShowContactModal(false)} className="absolute top-4 right-4 text-slate-400 hover:text-slate-600" data-testid="close-contact-modal-btn">
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
            </button>
            <h3 className="text-xl font-bold text-[#0A2540] mb-1">Request a Quote</h3>
            <p className="text-sm text-slate-500 mb-6">Tell us about your project and we'll get back to you promptly.</p>
            {contactSuccess ? (
              <div className="text-center py-8">
                <div className="w-14 h-14 bg-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-3">
                  <svg className="w-7 h-7 text-emerald-600" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" /></svg>
                </div>
                <p className="font-semibold text-[#0A2540]">Message Sent!</p>
                <p className="text-sm text-slate-500">We'll get back to you soon.</p>
              </div>
            ) : (
              <form onSubmit={handleContactSubmit} className="space-y-3">
                <input type="text" required value={contactForm.name} onChange={(e) => setContactForm({...contactForm, name: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="Name *" data-testid="modal-contact-name" />
                <input type="email" required value={contactForm.email} onChange={(e) => setContactForm({...contactForm, email: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="Email *" data-testid="modal-contact-email" />
                <input type="tel" value={contactForm.phone} onChange={(e) => setContactForm({...contactForm, phone: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="Phone (optional)" />
                <input type="text" value={contactForm.company} onChange={(e) => setContactForm({...contactForm, company: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none" placeholder="Company (optional)" />
                <textarea required rows={3} value={contactForm.message} onChange={(e) => setContactForm({...contactForm, message: e.target.value})} className="w-full px-4 py-2.5 border border-slate-200 rounded-xl text-sm focus:ring-2 focus:ring-amber-500 focus:border-amber-500 outline-none resize-none" placeholder="Project details *" data-testid="modal-contact-message" />
                <button type="submit" disabled={contactSubmitting} className="w-full py-2.5 rounded-full bg-amber-600 text-white text-sm font-semibold hover:bg-amber-700 transition-colors disabled:opacity-50" data-testid="modal-contact-submit">
                  {contactSubmitting ? 'Sending...' : 'Send Request'}
                </button>
              </form>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Bespoke;
